'use client';

import { BookOpen, Clock, DollarSign, ExternalLink, GraduationCap, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import GlassPanel from '@/components/dashboard/GlassPanel';
import type { PublicInstituteCourseOffering } from '@/lib/api/institute';

export interface CourseCardProps {
  offering: PublicInstituteCourseOffering;
  className?: string;
}

export function CourseCard({ offering, className }: CourseCardProps) {
  const feeDisplay = offering.fee
    ? `${offering.fee} ${offering.currency}`
    : 'Fee not specified';

  return (
    <GlassPanel
      noPadding
      className={cn(
        'group relative p-5 transition-all hover:shadow-md hover:border-neon-cyan/20',
        className
      )}
    >
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#0f2a4a]">
          <GraduationCap className="h-6 w-6 text-neon-amber" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                {offering.title || offering.course.courseName}
              </h3>
              <p className="mt-0.5 text-sm text-ash">{offering.institute.instituteName}</p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted">
            <span className="flex items-center gap-1">
              <BookOpen className="h-3.5 w-3.5 text-muted" />
              {offering.course.courseName}
            </span>
            {offering.deliveryMode && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5 text-muted" />
                {offering.deliveryMode}
              </span>
            )}
            <span className="flex items-center gap-1">
              <DollarSign className="h-3.5 w-3.5 text-muted" />
              {feeDisplay}
            </span>
            {offering.durationWeeks && (
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5 text-muted" />
                {offering.durationWeeks} weeks
              </span>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {offering.course.category && <Badge variant="info">{offering.course.category}</Badge>}
            <Badge variant={offering.status === 'published' ? 'success' : 'warning'}>
              {offering.status}
            </Badge>
          </div>

          {offering.description && (
            <p className="mt-3 text-sm text-ash line-clamp-2">{offering.description}</p>
          )}

          <div className="mt-4 flex items-center gap-3">
            <Button variant="outline" href={`/courses/${offering.id}`} className="shrink-0">
              <ExternalLink className="h-3.5 w-3.5" />
              Details
            </Button>
            <Button disabled className="shrink-0">
              Enquire
            </Button>
          </div>
        </div>
      </div>
    </GlassPanel>
  );
}
